import Button from '@doordash/component-button'
import {
  Colors,
  Icon,
  InlineChildren,
  Inset,
  Spacing,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { i18nKeys } from 'translationTypes'
import { submitNccCheckRequest } from '../../../../actions'
import {
  DX_BGC_NCC_CHECK_SHARE,
  DX_BGC_NCC_CONSUMER_INTEGRATION,
} from '../../../../experimentConstants'
import useHeightObserver from '../../../../hooks/useHeightObserver'
import i18n from '../../../../i18n'
import { addResizeObserverToElement } from '../../../../utilities/resizeObserver'
import { isMobile } from '../../../../utilities/userAgent'
import { loadUrl } from '../../../../webview'
import FullHeightWrapper from '../../../atoms/full-height-wrapper'
import SimpleSpacer from '../../../atoms/simple-spacer'
import StickyBottom from '../../../atoms/sticky-bottom'
import NccBackgroundCheckShareModal from '../../ncc-background-check-share'
import { BgcVendorComponentProps } from '../types'
import { getNccLinkPageConfig, NccLinkPageConfig, NCC_LOGO_URL } from './config'

const FooterStackChildren = styled(StackChildren)`
  padding-top: ${Spacing.XSmall}px;
`
const LogoContainer = styled(InlineChildren)`
  align-items: center;
  justify-content: center;
`
const StyledOl = styled.ol`
  padding-left: ${Spacing.Small}px;
`

interface NccBackgroundCheckLinkerProps extends BgcVendorComponentProps {
  submitNccCheck: (string) => string | null
  continueUrl?: string
  isLoading?: boolean
}

const getStartLink = (
  uniqueLink: string,
  linkConfig: NccLinkPageConfig['linkConfig']
) => {
  const { startUrl, hasEncodedClientRef = false } = linkConfig || {}

  if (!hasEncodedClientRef) {
    return startUrl
  }

  // base64 encode
  const encodedClientRef = btoa(`unique_link=${uniqueLink}`)
  return `${startUrl}?client_ref=${encodedClientRef}`
}

export const NccBackgroundCheckLinker: React.FC<NccBackgroundCheckLinkerProps> = ({
  countryCode,
  trackClick,
  uniqueLink,
  submitNccCheck,
  continueUrl,
  isLoading,
}) => {
  const [shareNccCheck, setShareNccCheck] = React.useState<boolean>(false)

  const {
    height: footerHeight,
    resizeObserver: footerResizeObserver,
  } = useHeightObserver()

  const { docStringKeys, linkConfig } = getNccLinkPageConfig(countryCode)

  React.useEffect(() => {
    if (isFeatureFlagEnabled(DX_BGC_NCC_CONSUMER_INTEGRATION)) {
      const fetchNccContinueUrl = async () => await submitNccCheck(uniqueLink)
      fetchNccContinueUrl()
    }
  }, [submitNccCheck, uniqueLink])

  const nccLink = continueUrl || getStartLink(uniqueLink, linkConfig)

  const handleClick = () => {
    trackClick({ link: nccLink })
    loadUrl(nccLink)
  }

  return (
    <>
      {shareNccCheck && (
        <NccBackgroundCheckShareModal onClose={() => setShareNccCheck(false)} />
      )}
      <FullHeightWrapper isMobile={isMobile}>
        <Inset>
          <StackChildren size={StackChildren.Sizes.Large}>
            <Text styles={Text.Styles.PageTitle2} tag="h1">
              {i18n.t(i18nKeys.backgroundCheck__nccTitle)}
            </Text>
            <Text color={Colors.TextSecondary}>
              {i18n.t(i18nKeys.backgroundCheck__nccSubTitle)}
            </Text>
            <StyledOl>
              <StackChildren size={StackChildren.Sizes.XxSmall}>
                {(docStringKeys || []).map(docStringKey => (
                  <Text color={Colors.TextSecondary} key={docStringKey}>
                    <li>{i18n.t(docStringKey)}</li>
                  </Text>
                ))}
              </StackChildren>
            </StyledOl>
          </StackChildren>
        </Inset>
        {isMobile && <SimpleSpacer $height={footerHeight} />}
        <StickyBottom isMobile={isMobile}>
          <Inset>
            <FooterStackChildren
              ref={addResizeObserverToElement(footerResizeObserver)}
              size={StackChildren.Sizes.Large}
            >
              <Button
                href={nccLink}
                tag="a"
                target="_blank"
                trailingIcon={Icon.Types.LinkExternalLine}
                state={
                  isLoading ? Button.States.Loading : Button.States.Default
                }
                onClick={handleClick}
              >
                {continueUrl
                  ? i18n.t(i18nKeys.backgroundCheck__nccCompleteBgcButton)
                  : i18n.t(i18nKeys.backgroundCheck__nccStartBgcButton)}
              </Button>
              {isFeatureFlagEnabled(DX_BGC_NCC_CHECK_SHARE) && (
                <Button
                  size={Button.Sizes.Small}
                  type={Button.Types.Link}
                  onClick={() => setShareNccCheck(true)}
                >
                  {i18n.t(i18nKeys.backgroundCheck__nccShareBgcButton)}
                </Button>
              )}
              <LogoContainer>
                <Text styles={Text.Styles.Caption2} color={Colors.TextTertiary}>
                  {i18n.t(i18nKeys.backgroundCheck__nccPoweredBy)}
                </Text>
                <img
                  src={NCC_LOGO_URL}
                  alt={i18n.t(i18nKeys.backgroundCheck__nccLogo)}
                />
              </LogoContainer>
            </FooterStackChildren>
          </Inset>
        </StickyBottom>
      </FullHeightWrapper>
    </>
  )
}

const mapStateToProps = state => ({
  continueUrl: state.backgroundCheckExternalLink.continueUrl,
  isLoading: state.backgroundCheckExternalLink.isLoading,
})

const mapDispatchToProps = {
  submitNccCheck: submitNccCheckRequest,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NccBackgroundCheckLinker)
